// SaaS plans & owner purchase — API client.
// Backend: rentalapi/routes/plans.js (Docs/requirements/common/plans/plansandpurchase).
// Not vertical-scoped — a subscription is per owner account.

import { client } from './api';

// ---- Public pricing page (§7) ----
export async function getPricing() {
  const res = await client.get('/api/plans');
  return res.data.data; // { moduleEnabled, plans, featureCatalogue, owner }
}

// ---- Owner subscription lifecycle (§8–§19) ----
export async function recordSubscriptionVisit() {
  const res = await client.post('/api/subscriptions/dashboard-visit');
  return res.data.data;
}

export async function getMySubscription() {
  const res = await client.get('/api/subscriptions/me');
  return res.data.data;
}

export async function activateFree(planCode) {
  const res = await client.post('/api/subscriptions/activate-free', { planCode });
  return res.data.data;
}

export async function createSubscriptionOrder(planCode, billingCycle = 'MONTHLY', couponCode = '') {
  const res = await client.post('/api/subscriptions/order', { planCode, billingCycle, couponCode });
  return res.data.data; // { orderId, amount, currency, paymentMode: 'gateway'|'manual', ... }
}

// membership_approval_flow.md — the member submits a cash / bank-transfer payment claim. This does
// NOT activate anything; it parks the subscription in PENDING_APPROVAL for the website admin to
// verify and approve.
export async function requestActivation({ orderId = '', paymentMethod, claimedReference, claimedPaidAt, ownerNote }) {
  const res = await client.post('/api/subscriptions/request-activation', {
    orderId, paymentMethod, claimedReference,
    ...(claimedPaidAt ? { claimedPaidAt } : {}),
    ...(ownerNote ? { ownerNote } : {})
  });
  return res.data.data;
}

// The verified online-gateway callback (Razorpay-shaped). Auto-activates with no admin step.
// Server 501s until a gateway is actually configured.
export async function verifyGatewayPayment({ orderId = '', paymentReference, method = 'gateway' }) {
  const res = await client.post('/api/subscriptions/gateway/verify', { orderId, paymentReference, method });
  return res.data.data;
}

export async function changePlan(planCode, billingCycle) {
  const res = await client.post('/api/subscriptions/change', { planCode, billingCycle });
  return res.data.data; // { kind: 'upgrade'|'downgrade'|'same', ... }
}

export async function cancelSubscription(atPeriodEnd = true, reason = '') {
  const res = await client.post('/api/subscriptions/cancel', { atPeriodEnd, reason });
  return res.data.data;
}

export async function getSubscriptionEvents() {
  const res = await client.get('/api/subscriptions/events');
  return res.data.data;
}

// manual_vs_automatic_invoicing.md — the platform membership tax invoices raised to this owner.
export async function getSubscriptionInvoices() {
  const res = await client.get('/api/subscriptions/invoices');
  return res.data.data;
}

// The owner's billing identity that goes on those invoices (GSTIN + legal name).
export async function getBillingDetails() {
  const res = await client.get('/api/auth/me');
  return { billingName: res.data.user.billingName || '', gstin: res.data.user.gstin || '' };
}
export async function saveBillingDetails({ billingName, gstin }) {
  const res = await client.patch('/api/auth/me', { billingName, gstin });
  return res.data.user;
}

// Blob download (the axios interceptor adds the auth header a plain <a href> can't carry).
export async function downloadSubscriptionInvoice(id, invoiceNumber) {
  const res = await client.get(`/api/subscriptions/invoices/${id}/pdf`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `invoice-${String(invoiceNumber || id).replace(/[^a-z0-9-]+/gi, '_')}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// ---- Admin: plan & feature management (§3–§6), oversight (§21), audit (§22) ----
export async function adminListPlans() {
  const res = await client.get('/api/admin/plans');
  return res.data.data;
}

export async function adminCreatePlan(payload) {
  const res = await client.post('/api/admin/plans', payload);
  return res.data.data;
}

export async function adminUpdatePlan(id, payload) {
  const res = await client.patch(`/api/admin/plans/${id}`, payload);
  return res.data.data;
}

export async function adminSetPlanStatus(id, status) {
  const res = await client.patch(`/api/admin/plans/${id}/status`, { status });
  return res.data.data;
}

export async function adminListFeatures() {
  const res = await client.get('/api/admin/features');
  return res.data.data;
}

export async function adminCreateFeature(payload) {
  const res = await client.post('/api/admin/features', payload);
  return res.data.data;
}

export async function adminUpdateFeature(id, payload) {
  const res = await client.patch(`/api/admin/features/${id}`, payload);
  return res.data.data;
}

export async function adminListSubscriptions(params = {}) {
  const res = await client.get('/api/admin/subscriptions', { params });
  return res.data; // { data, pagination }
}

export async function adminSubscriptionAnalytics() {
  const res = await client.get('/api/admin/subscriptions/analytics');
  return res.data.data;
}

export async function adminActivateForOwner(payload) {
  const res = await client.post('/api/admin/subscriptions/activate', payload);
  return res.data.data;
}

// membership_approval_flow.md — the website admin's one-page membership view.
export async function adminSubscriptionOverview() {
  const res = await client.get('/api/admin/subscriptions/overview');
  return res.data.data; // { subscribed, expiring, due, expiringWindowDays }
}

export async function adminApproveActivation(id, body = {}) {
  const res = await client.post(`/api/admin/subscriptions/${id}/approve`, body);
  return res.data.data;
}

export async function adminRejectActivation(id, reason = '') {
  const res = await client.post(`/api/admin/subscriptions/${id}/reject`, { reason });
  return res.data.data;
}

export async function adminSendActivationToFinance(id, note = '') {
  const res = await client.post(`/api/admin/subscriptions/${id}/send-to-finance`, { note });
  return res.data.data;
}

export async function financeReviewActivation(id, { recommendation, note = '' }) {
  const res = await client.post(`/api/admin/subscriptions/${id}/finance-review`, { recommendation, note });
  return res.data.data;
}

export async function adminSubscriptionEvents(params = {}) {
  const res = await client.get('/api/admin/subscription-events', { params });
  return res.data.data;
}

export async function adminGetPlanConfig() {
  const res = await client.get('/api/admin/plan-config');
  return res.data.data;
}

export async function adminUpdatePlanConfig(payload) {
  const res = await client.put('/api/admin/plan-config', payload);
  return res.data.data;
}

// A plan feature value ("true" / a number / "UNLIMITED") as a display string.
export function featureValueLabel(value) {
  if (value === true) return '✓';
  if (value === false || value === 0 || value == null) return '—';
  if (String(value).toUpperCase() === 'UNLIMITED') return 'Unlimited';
  return String(value);
}

export function money(amount, currency = 'INR') {
  const n = Number(amount) || 0;
  try {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 0 }).format(n);
  } catch {
    return `${currency} ${n}`;
  }
}

export async function adminGrantDecisionDays(ownerId, days, reason) {
  const res = await client.post('/api/admin/subscriptions/decision-days', { ownerId, days, reason });
  return res.data.data;
}
